"use client";

import { useEffect } from "react";
import { setUser, setTag } from "@sentry/nextjs";
import { useSession } from "@/lib/auth/auth-client";
import { installClientConsoleErrorHook } from "@/lib/observability/console-hooks";
import { installClientFetchHook } from "@/lib/observability/fetch-hooks";
import {
  clearClientErrorUser,
  reportClientError,
  setClientErrorUser,
  setClientNetworkContext,
} from "@/lib/observability/report-client-error";
import type { ObservabilityContextResponse } from "@/lib/observability/types";
import { getClientPlatformContext } from "@/lib/analytics/platform-context";
import { isSentryReportingEnabled } from "@/lib/observability/sentry-env";

function syncSentryUser(user: { id: string; email?: string } | null): void {
  if (!isSentryReportingEnabled()) {
    clearClientErrorUser();
    return;
  }

  if (!user) {
    clearClientErrorUser();
    setUser(null);
    return;
  }

  const id = user.id || "anonymous";
  const email = user.email || undefined;

  setClientErrorUser({ id, email });
  setUser({ id, email });
}

async function hydrateNetworkContext(attempt = 0): Promise<void> {
  try {
    const response = await fetch("/api/observability/context/", {
      method: "GET",
      cache: "no-store",
      credentials: "include",
    });

    if (!response.ok) {
      return;
    }

    const data = (await response.json()) as ObservabilityContextResponse;
    setClientNetworkContext({
      ip: data.ip,
      country: data.country,
      dnsHost: data.dnsHost,
    });
  } catch (error) {
    // Retry once after 3 seconds — this endpoint is excluded from global
    // fetch-hook retries so a single transient failure at page load would
    // permanently degrade Sentry context for the entire session.
    if (attempt < 1) {
      setTimeout(() => void hydrateNetworkContext(attempt + 1), 3_000);
      return;
    }
    reportClientError(error, {
      source: "ErrorReportingProvider.hydrateNetworkContext",
      severity: "warning",
    });
  }
}

export function ErrorReportingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: session } = useSession();

  useEffect(() => {
    // In Electron, skip the console.error and global fetch hooks.
    // Blocked analytics requests generate console errors that feed into the
    // console hook → Sentry → fetch hook cascade, starving the event loop.
    // Sentry's globalHandlers integration still captures unhandled errors.
    const isDesktop =
      typeof window !== "undefined" && window.electronAPI !== undefined;
    if (!isDesktop) {
      installClientConsoleErrorHook();
      installClientFetchHook();

      if (!isSentryReportingEnabled()) {
        return;
      }

      // Set platform/OS/browser as global Sentry tags so ALL events (including
      // auto-captured ones) carry this context, not just manually reported errors.
      const ctx = getClientPlatformContext();
      setTag("user_platform", ctx.platform);
      setTag("user_os", ctx.os);

      void hydrateNetworkContext();
    }
  }, []);

  // Sync Sentry user when session changes
  useEffect(() => {
    syncSentryUser(session?.user ?? null);
  }, [session?.user]);

  return <>{children}</>;
}
